import { vec3 } from "gl-matrix";
import { SceneLighting } from "../common";
import {
  easeInCubic,
  easeInExp,
  easeInOutCubic,
  easeInSine,
  easeOutBack,
  easeOutCubic,
  lerp,
} from "../interpolation";
import {
  CumulativePieceFn,
  PieceFn,
  StatefulSmoothStepSequencer,
} from "../PieceFn";
import { createCameraPath } from "../CameraPath";
import { clamp01 } from "../math";
import { createScene } from "../sequencer";

const SCENE_BLACK = 0;
const SCENE_MACARONI_FACTORY = 1;
const SCENE_TUBE = 2;
const SCENE_MACARONI_FLYBY = 3;
const SCENE_LASAGA_WORLD = 4;
const SCENE_TESSERACT = 5;

export const defaultLighting: SceneLighting = {
  ambient: [0.2, 0.2, 0.2],
  light1: {
    position: [0, 0, 0],
    intensity: [0.5, 0.55, 0.6],
  },
  cameraLightIntensity: [0.5, 0.55, 0.6],
  fogColor: [0.1098039216, 0.1647058824, 0.3882352941],
  fogDensity: 1.5,
};

const factoryLighting: SceneLighting = {
  ...defaultLighting,
  fogDensity: 1.4,
  fogColor: [0.13, 0.1, 0.1],
  light1: {
    position: [0, -10, 0],
    intensity: [1.0, 0, 0.1],
  },
  ambient: [0.15, 0.15, 0.15],
};

const moldArmFn = new PieceFn()
  // From 0 to 24 beats, the mold is open (3)
  .addSegment(0, 24, () => 3)
  // From 56 to 64 beats, the mold closes (3 -> 0)
  .addSegment(56, 64, (t) => 3 - easeInOutCubic(t) * 3)
  // From 68 to 76, the mold opens (0 -> 30)
  .addSegment(68, 76, (t) => easeInExp(t) * 30);

const doughPosFn = new PieceFn()
  // Dough balls approach between 0 and 48 beats
  .addSegment(0, 48, (t) => 0.5 - easeOutCubic(t) * 0.5)
  // When turned to macaroni, stay up for a moment
  .addSegment(64, 80, () => 0)
  // And then start falling down
  .addSegment(76, 96, (t) => easeInSine(Math.pow(t, 1.0005)));

const doughOrMacaroniFn = new PieceFn()
  .addSegment(0, 64, () => 0)
  // Turn to macaroni at 64 beats
  .addSegment(64, 128, () => 1);

const defaultFadeIn = new PieceFn().addSegment(0, 8, (t) => 1 - t);

const mainSceneEndFadeOut = new PieceFn().addSegment(84, 88, (t) =>
  easeInCubic(t),
);

const sceneYRotation = new PieceFn()
  .addSegment(0, 80, () => 0)
  .addSegment(80, 88, (t) => easeInCubic(t));

const mainScene = createScene({
  name: "Macaroni Factory",
  shader: "macaroniFactory",
  sceneId: SCENE_MACARONI_FACTORY,
  length: 96,
  lighting: {
    ...factoryLighting,
  },

  variables: (ctx) => {
    const moldArmPos = moldArmFn.evaluate(ctx.sceneNowInBeats);
    const doughPos = doughPosFn.evaluate(ctx.sceneNowInBeats);
    const doughOrMacaroni = doughOrMacaroniFn.evaluate(ctx.sceneNowInBeats);
    const yRotation = sceneYRotation.evaluate(ctx.sceneNowInBeats);

    return [moldArmPos, doughOrMacaroni, doughPos, yRotation];
  },
}).addSequence([
  {
    // Flyby from above
    length: 24,
    camera: createCameraPath([
      {
        position: [10, 0, 25],
        target: [0, 0, -30],
      },
      {
        position: [10, 3, 30],
        target: [-2, 0, -31],
      },
      {
        position: [10, 8, 0],
        target: [-2, 5, -32],
      },
      {
        position: [10, 2, 0],
        target: [-2, -5, -30],
      },
    ]),
    fade: (t) => [
      0,
      0,
      0,
      defaultFadeIn.evaluate(t.sceneContext.sceneNowInBeats),
    ],
    text: [
      {
        text: "HYVINKAEAE",
        position: [0.9, -0.7],
        font: "novarese",
        align: "right",
        timing: {
          enter: 4,
          exit: 24,
        },
        type: "horizontal",
      },
      {
        text: "2023",
        position: [0.86, -0.8],
        timing: {
          enter: 4,
          exit: 24,
        },
        scale: [0.001, 0.001],
        font: "novarese",
        align: "right",
        type: "horizontal",
      },
      {
        text: "?",
        position: {
          from: [1.1, -0.8],
          to: [0.9, -0.8],
          ease: (t) => easeOutCubic(clamp01(t * 4.0)),
        },
        timing: {
          enter: 8,
          exit: 24,
        },
        scale: [0.001, 0.001],
        font: "novarese",
        align: "right",
        type: "horizontal",
      },
    ],
  },
  {
    assertStart: 24,
    length: 46,
    camera: createCameraPath([
      {
        position: [10, 1, 20],
        target: [-31, 0, 30],
      },
      {
        position: [10, 2, 20],
        target: [-30, 0, 30],
      },
      {
        position: [10, 2, 50],
        target: [-30, 2, 30],
      },
      {
        position: [-10, 10, 50],
        target: [0, 2, 0],
        zoom: 1.0,
      },
      {
        position: [-15, 7, 20],
        target: [0, 1, 0],
        zoom: 2.0,
      },
      {
        position: [-10, 4, 20],
        target: [0, 0.5, 0],
        zoom: 2.0,
      },
      {
        position: [0, 5, 5],
        target: [0, 0, 1],
      },
    ]),
    text: [
      {
        text: "CHAPTER 0",
        position: [-0.9, 0.8],
        font: "novarese",
        align: "left",
        timing: {
          enter: 0,
          exit: 24,
        },
        type: "horizontal",
      },
      {
        text: "HOW THE MAKARON IS MADE",
        position: [-0.9, 0.7],
        timing: {
          enter: 0,
          exit: 24,
        },
        scale: [0.001, 0.001],
        font: "novarese",
        align: "left",
        type: "horizontal",
      },
    ],
  },
  {
    assertStart: 70,
    length: 4,
    camera: {
      position: [5, 5, 10],
      target: [0, 0, 0],
    },
  },
  {
    length: 22,
    assertStart: 74,
    camera: createCameraPath([
      {
        position: [5, 6, 10],
        target: [0, 1, 0],
      },
      {
        position: [5, 5, 10],
        target: [0, 0, 0],
      },
      {
        position: [0, 10, 2],
        target: [0, -10, 0],
      },
      {
        position: [0, -10, 0],
        target: [0, -11, 0],
      },
      {
        position: [0, -50, 0],
        target: [0, -50, 0],
      },
      {
        position: [0, -55, 0],
        target: [0, -100, 0],
      },
    ]),
    fade: (t) => [
      0,
      0,
      0,
      mainSceneEndFadeOut.evaluate(t.sceneContext.sceneNowInBeats),
    ],
    text: [
      {
        text: "CHAPTER 1",
        position: [-0.9, 0.8],
        font: "novarese",
        align: "left",
        timing: {
          enter: 8,
          exit: 22,
        },
        type: "horizontal",
      },
      {
        text: "THE FALL",
        position: [-0.9, 0.7],
        timing: {
          enter: 8,
          exit: 22,
        },
        scale: [0.001, 0.001],
        font: "novarese",
        align: "left",
        type: "horizontal",
      },
    ],
  },
]);

// prettier-ignore
const synthPattern =  [
  0, 0, 1, 0, 0, 0, 0, 0,
  1, 0, 0, 1, 0, 0, 0, 0,
  0, 0, 1, 0, 0, 0, 0, 0,
  1, 0, 0, 1, 0, 0, 0, 0,
  0, 0, 0, 0, 0, 0, 0, 0,
  0, 0, 0, 0, 0, 0, 0, 0,
  0, 0, 0, 0, 0, 0, 0, 0,
  0, 0, 0, 0, 0, 0, 0, 0,

  0, 0, 1, 0, 0, 0, 0, 0,
  1, 0, 0, 1, 0, 0, 0, 0,
  0, 0, 1, 0, 0, 0, 0, 0,
  1, 0, 0, 1, 0, 0, 0, 0,
  0, 0, 0, 0, 0, 0, 0, 0,
  0, 0, 0, 0, 0, 0, 0, 0,
  0, 0, 0, 0, 0, 0, 0, 0,
  1, 0, 0, 0, 0, 0, 0, 0,

  0, 0, 1, 0, 0, 0, 0, 0,
  1, 0, 0, 1, 0, 0, 0, 0,
  0, 0, 1, 0, 0, 0, 0, 0,
  1, 0, 0, 1, 0, 0, 0, 0,
  0, 0, 0, 0, 0, 0, 0, 0,
  0, 0, 0, 0, 0, 0, 0, 0,
  0, 0, 0, 0, 0, 0, 0, 0,
  0, 0, 0, 0, 0, 0, 0, 0,

  0, 0, 1, 0, 0, 0, 0, 0,
  1, 0, 0, 1, 0, 0, 0, 0,
  0, 0, 1, 0, 0, 0, 0, 0,
  1, 0, 0, 1, 0, 0, 0, 0,
  0, 0, 0, 0, 0, 0, 0, 0,
  0, 0, 0, 0, 0, 0, 0, 0,
  0, 0, 0, 0, 0, 0, 0, 0,
  0, 0, 0, 0, 0, 0, 0, 0,
]

// prettier-ignore
const subPattern = [
  1, 0, 0, 0, 0, 0, 1, 0,
  0, 0, 0, 0, 0, 0, 0, 0,
  0, 0, 0, 0, 0, 0, 0, 0,
  0, 0, 0, 0, 0, 0, 0, 0,
  1, 0, 0, 0, 0, 0, 1, 0,
  0, 0, 0, 0, 0, 0, 0, 0,
  0, 0, 0, 0, 0, 0, 0, 0,
  0, 0, 0, 0, 0, 0, 0, 0,
]

const synthPatternFuzz = new StatefulSmoothStepSequencer(0.5, 2, synthPattern);
const synthPatternLight = new StatefulSmoothStepSequencer(1, 8, synthPattern);
const synthPatternLight2 = new StatefulSmoothStepSequencer(
  0.5,
  16,
  synthPattern,
);

const subPatternFuzz = new StatefulSmoothStepSequencer(1, 8, subPattern);
const subPatternPump = new StatefulSmoothStepSequencer(0.5, 8, subPattern);

const tunnelExpand = new PieceFn()
  .addSegment(0, 32, () => 0)
  .addSegment(16, 32, (t) => easeInCubic(t))
  .addSegment(32, 48, (t) => 1 - easeInCubic(t));

const tunnelEndGlitchIntoNothingness = new PieceFn()
  .addSegment(0, 32, () => 0)
  .addSegment(32, 48, (t) => easeInCubic(t) * 0.1)
  .addSegment(56, 64, (t) => easeInCubic(t));

const tunnelEndInstant = new PieceFn()
  .addSegment(0, 32, () => 0)
  .addSegment(48, 64, (t) => 1.0);

const tunnelFadeToWhite = new PieceFn()
  .addSegment(0, 62, () => 0)
  .addSegment(62, 64, (t) => easeInCubic(t));

const tunnelFadeIn = new PieceFn()
  .addSegment(0, 2, (t) => 1 - easeInCubic(t))
  .addSegment(2, 32, () => 0);

const tubeScene = mainScene
  .then({
    sceneId: SCENE_TUBE,
    shader: "macaroniFactory",
    length: 64,
    name: "Through the tube",
    lighting: {
      // @ts-ignore
      ambient: (ctx): vec3 => {
        const endLight: vec3 = [0.9, 0.9, 1.0];
        return vec3.lerp(
          [0, 0, 0],
          factoryLighting.ambient,
          endLight,
          tunnelEndGlitchIntoNothingness.evaluate(ctx.sceneNowInBeats) * 0.05,
        );
      },
      cameraLightIntensity: [0.1, 0.1, 0.1],
      // @ts-ignore
      fogColor: (ctx): vec3 => {
        // White fog at the end
        const endFog: vec3 = [1.0, 1.0, 1.0];
        return vec3.lerp(
          [0, 0, 0],
          factoryLighting.fogColor,
          endFog,
          tunnelEndInstant.evaluate(ctx.sceneNowInBeats),
        );
      },
      fogDensity: (ctx): number => {
        return lerp(
          factoryLighting.fogDensity,
          0.8,
          tunnelEndInstant.evaluate(ctx.sceneNowInBeats),
        );
      },
      light1: {
        position: [0, 0, 0],
        // @ts-ignore
        intensity: (ctx): vec3 => {
          const kickSignal = synthPatternLight.evaluate(ctx.sceneNowInBeats);
          return vec3.scale(
            [0, 0, 0],
            factoryLighting.light1.intensity,
            kickSignal,
          );
        },
      },
    },
    variables: (ctx) => {
      const kickSignal = synthPatternFuzz.evaluate(ctx.sceneNowInBeats);
      const macaroniGlitch =
        easeOutCubic(kickSignal) +
        tunnelEndGlitchIntoNothingness.evaluate(ctx.sceneNowInBeats) * 10.0;
      const tunnelExpansion = tunnelExpand.evaluate(ctx.sceneNowInBeats);
      return [macaroniGlitch, tunnelExpansion, 0, 0];
    },
  })
  .addSequence([
    {
      assertStart: 0,
      text: [
        {
          text: "MAKARON",
          position: {
            from: [-1.5, 0.6],
            to: [0.0, 0.0],
          },
          font: "pasta",
          params: {
            distortion: 1,
            waviness: 2.1,
          },
          type: "horizontal",
        },
        {
          text: "CRISIS",
          position: {
            from: [1.5, -1.0],
            to: [0.0, -0.3],
          },
          font: "pasta",
          params: {
            distortion: 1.0,
            waviness: 2.1,
          },
          type: "horizontal",
        },
      ],
      camera: {
        position: {
          from: [-1, -1, -80],
          to: [1, 1.5, 0],
        },
        target: {
          from: [0, 0, 0],
          to: [0, 0, 10],
        },
      },
      length: 32,
      fade: (ctx) => {
        const value = tunnelFadeIn.evaluate(ctx.sceneContext.sceneNowInBeats);
        return [0, 0, 0, value];
      },
    },
    {
      assertStart: 32,
      length: 16,
      camera: {
        position: {
          from: [1, 2, 5],
          to: [0, 0, 50],
        },
        target: {
          from: [1, 0, 0],
          to: [0, 1, -10],
        },
      },
      text: [
        {
          text: "MAC & CHEESY MUSIC",
          position: [-0.9, -0.7],
          font: "novarese",
          align: "left",
          type: "horizontal",
        },
        {
          text: "MAKARONI SIZE",
          position: {
            from: [-0.7, -0.8],
            to: [-0.9, -0.8],
            ease: (t) => easeOutBack(clamp01(t * 4.0)),
          },
          timing: {
            enter: 4,
            exit: 16,
          },
          scale: [0.001, 0.001],
          font: "novarese",
          align: "left",
          type: "horizontal",
        },
      ],
    },
    {
      assertStart: 48,
      length: 16,
      camera: {
        position: {
          from: [0.1, 0, -10],
          to: [0.2, 0.5, -50],
        },
        target: [-0.17, 0, 10],
      },
      backgroundColor: () => [1.0, 1.0, 1.0],
      fade: (ctx) => {
        const intensity = tunnelFadeToWhite.evaluate(
          ctx.sceneContext.sceneNowInBeats,
        );
        return [1, 1, 1, intensity];
      },
      text: [
        {
          text: "SPAGHETTI CODE",
          position: [0.9, 0.7],
          font: "novarese",
          align: "right",
          params: {
            color: [0.0, 0.0, 0.0],
          },
          type: "horizontal",
        },
        {
          text: "MAKARONIN HAJUINEN MIES",
          position: {
            from: [1.1, 0.6],
            to: [0.9, 0.6],
            ease: (t) => easeOutBack(clamp01(t * 4.0)),
          },
          timing: {
            enter: 4,
            exit: 16,
          },
          scale: [0.001, 0.001],
          font: "novarese",
          align: "right",
          params: {
            color: [0.0, 0.0, 0.0],
          },
          type: "horizontal",
        },
        {
          text: "PENNE HERMANNI",
          position: {
            from: [1.1, 0.52],
            to: [0.9, 0.52],
            ease: (t) => easeOutBack(clamp01(t * 4.0)),
          },
          timing: {
            enter: 5,
            exit: 16,
          },
          scale: [0.001, 0.001],
          font: "novarese",
          align: "right",
          params: {
            color: [0.0, 0.0, 0.0],
          },
          type: "horizontal",
        },
      ],
    },
  ]);

const makaronDimensionFadeInOut = new PieceFn()
  .addSegment(0, 4, (t) => 1 - t)
  .addSegment(60, 64, (t) => easeInCubic(t));
const compressMakarons = new PieceFn()
  .addSegment(0, 48, () => 1)
  .addSegment(48, 64, (t) => 1 - easeInCubic(t));

const makaronDimensionFogColor = new PieceFn<vec3>()
  .addSegment(0, 32, () => defaultLighting.fogColor)
  .addSegment(32, 48, () => [0.34, 0, 0])
  .addSegment(48, 16, () => [0, 0.15, 0.03]);

const makaronDimensionScene = tubeScene
  .then({
    length: 64,
    lighting: {
      ...defaultLighting,
      fogColor: (t) =>
        makaronDimensionFogColor.evaluate(t.sceneNowInBeats) as [
          number,
          number,
          number,
        ],
      cameraLightIntensity: (ctx) => {
        return [1.0, 1.0, 1.0].map(
          () => 0.7 + synthPatternLight2.evaluate(ctx.sceneNowInBeats) * 2.5,
        ) as [number, number, number];
      },
    },
    name: "Welcome to Makaron Dimension",
    sceneId: SCENE_MACARONI_FLYBY,
    shader: "main",
    variables: (ctx) => {
      const subSignal = subPatternFuzz.evaluate(ctx.sceneNowInBeats);
      const compress = compressMakarons.evaluate(ctx.sceneNowInBeats);
      const leadSignal = synthPatternFuzz.evaluate(ctx.sceneNowInBeats);
      return [subSignal, compress, leadSignal, 0];
    },
    text: [
      {
        text: "CHAPTER 2",
        position: [-0.9, 0.8],
        font: "novarese",
        align: "left",
        timing: {
          enter: 8,
          exit: 22,
        },
        type: "horizontal",
      },
      {
        text: "THE GATHERING",
        position: [-0.9, 0.7],
        timing: {
          enter: 8,
          exit: 22,
        },
        scale: [0.001, 0.001],
        font: "novarese",
        align: "left",
        type: "horizontal",
      },
    ],
  })
  .addSequence([
    {
      camera: createCameraPath([
        {
          position: [0, 1.5, -20],
          target: [0, 0, 0],
          rotationY: 0,
          zoom: 0.5,
        },
        {
          position: [20, -5.5, -30],
          target: [0, 0, 0],
          rotationY: 0,
        },
        {
          position: [20, -5, 50],
          target: [0, 0, 0],
          rotationY: 0,
        },
        {
          position: [20, -5.5, -30],
          target: [0, 0, 0],
          rotationY: 0,
          zoom: 2.0,
        },
        {
          position: [20, -30, -20],
          target: [0, 0, 0],
          rotationY: 0,
        },
      ]),
      length: 64,
      backgroundColor: () => [8 / 255, 29 / 255, 74 / 255],
      fade: (ctx) => [
        1,
        1,
        1,
        makaronDimensionFadeInOut.evaluate(ctx.sceneContext.sceneNowInBeats),
      ],
    },
  ]);

const compressLasaga = new PieceFn()
  .addSegment(0, 48, () => 1)
  .addSegment(48, 64, (t) => 1 - easeInCubic(t));

const greetz =
  "iquilezles | Jumalauta | Magnetismin Tietokonekerho | Kesaekissakvartetti | Myllyn Paras | Barilla | Rummo | Dekadence | De Cecco | Pirkka Parhaat | Elites";

const macaroniMachineFadeInOut = new PieceFn()
  .addSegment(0, 4, (t) => 1 - t)
  .addSegment(60, 64, (t) => easeInCubic(t));

const macaroniMachine = makaronDimensionScene
  .then({
    length: 64,
    lighting: {
      ...defaultLighting,
      fogColor: [0.32, 0.03, 0.0],
      light1: {
        position: [0, 0, 0],
        intensity: [0.32, 0.03, 0],
      },
      //cameraLightIntensity: [0.85, 0.41, 0],
      cameraLightIntensity: (ctx) => {
        const lead = synthPatternLight.evaluate(ctx.sceneNowInBeats);
        const intensity = 0.1 + lead * 0.5;
        return [intensity * 1.0, intensity, intensity * 0.01];
      },
    },
    name: "Macaroni Machine",
    shader: "macaroniMachine",
    variables: (ctx) => {
      const subSignal = subPatternPump.evaluate(ctx.sceneNowInBeats);
      return [subSignal, 0, 0, 0];
    },
    text: [
      {
        text: "MAKARONIN YSTAVAET GREETS",
        position: [-0.95, 0.8],
        scale: [0.001, 0.001],
        font: "novarese",
        align: "left",
        timing: {
          enter: 0,
          exit: 16,
        },
        type: "horizontal",
        params: {
          color: [0.9, 0.79, 0.39],
        },
      },
      {
        text: greetz,
        position: {
          from: [10.0, -0.8],
          to: [-10.0, -0.8],
        },
        font: "pasta",
        params: {
          distortion: 1.0,
          waviness: 1.1,
        },
        type: "horizontal",
      },
    ],
  })
  .addSequence([
    {
      length: 64,
      camera: createCameraPath([
        {
          position: [1, 0, 0.0],
          target: [0, 0, 10],
        },
        {
          position: [1, 0, 2.0],
          target: [0, 0, 11],
        },
        {
          position: [1, 10.0, 2.5],
          target: [0, 0, 0.75],
        },
        {
          position: [1, 10.5, 2.5],
          target: [0, 0, 1],
        },
      ]),
      fade: (ctx) => {
        return [
          1,
          1,
          1,
          macaroniMachineFadeInOut.evaluate(ctx.sceneContext.sceneNowInBeats),
        ];
      },
    },
  ]);

const macaroniBoxSize = new PieceFn()
  .addSegment(0, 8, (t) => easeOutBack(t))
  .addSegment(8, 30, () => 1)
  .addSegment(30, 32, (t) => 1.0 - easeOutCubic(t));

const macaroniHeartRepeat = new PieceFn()
  .addSegment(0, 48, () => 0)
  .addSegment(48, 64, (t) => easeInCubic(t));

// prettier-ignore
const amenBreakKickPattern = [
  1, 0, 0, 0, 0, 0, 0, 0,
  0, 0, 0, 0, 0, 0, 0, 0,
  1, 0, 1, 0, 0, 0, 0, 0,
  0, 0, 0, 0, 0, 0, 0, 0,
  1, 0, 0, 0, 0, 0, 0, 0,
  0, 0, 0, 0, 0, 0, 0, 0,
  1, 0, 1, 0, 0, 0, 0, 0,
  0, 0, 0, 0, 0, 0, 0, 0,
]

const amenBreakKick = new StatefulSmoothStepSequencer(
  0.5,
  8,
  amenBreakKickPattern,
);

const amenBreakKickFilter = new PieceFn()
  .addSegment(0, 1, (t) => t)
  .addSegment(1, 48, () => 1)
  .addSegment(47, 48, (t) => 1 - easeInCubic(t))
  .addSegment(48, 64, (t) => 0);

const macaroniBoxSceneFadeOut = new PieceFn()
  .addSegment(0, 62, () => 0)
  .addSegment(62, 64, (t) => easeInCubic(t));

const macaroniBoxScene = macaroniMachine
  .then({
    length: 64,
    lighting: {
      ...defaultLighting,
      fogDensity: 0.5,
      ambient: [0.2, 0.2, 0.2],
      cameraLightIntensity: [5, 5, 5],
      light1: {
        position: [0, 5, 0],
        intensity: [0.9, 0.9, 0.9].map((x) => x * 10) as [
          number,
          number,
          number,
        ],
      },
    },
    variables: (ctx) => {
      return [
        macaroniBoxSize.evaluate(ctx.sceneNowInBeats),
        macaroniHeartRepeat.evaluate(ctx.sceneNowInBeats),
        amenBreakKick.evaluate(ctx.sceneNowInBeats) *
          amenBreakKickFilter.evaluate(ctx.sceneNowInBeats),
        0.0,
      ];
    },
    name: "Macaroni Box",
    shader: "macaroniBox",
    text: [
      {
        text: "MAKARON",
        position: [-0.85, -0.2],
        font: "pasta",
        type: "vertical",
        verticalSpacing: 25.5,
        spaceHeight: 50,
        params: {
          distortion: 1.0,
          waviness: 1.1,
          color: [1.0, 0.0, 0.0],
        },
      },
      {
        text: "BOX",
        position: [0.85, 0],
        font: "pasta",
        type: "vertical",
        verticalSpacing: 1.5,
        spaceHeight: 50,
        params: {
          distortion: 1.0,
          waviness: 1.1,
        },
        timing: {
          enter: 0,
          exit: 30,
        },
      },
      {
        text: "DONUT",
        position: [0.85, -0.2],
        font: "pasta",
        type: "vertical",
        verticalSpacing: 1.5,
        spaceHeight: 50,
        params: {
          distortion: 1.0,
          waviness: 1.1,
        },
        timing: {
          enter: 32,
          exit: 46,
        },
      },
      {
        text: "LOVE",
        position: [0.85, -0.2],
        font: "pasta",
        type: "vertical",
        verticalSpacing: 1.5,
        spaceHeight: 50,
        params: {
          distortion: 1.0,
          waviness: 1.1,
        },
        timing: {
          enter: 48,
          exit: 64,
        },
      },
    ],
  })
  .addSequence([
    {
      length: 64,
      camera: createCameraPath([
        {
          position: [20, 20, -20],
          target: [0, 0.0, 0],
        },
        {
          position: [-20, 5, 10],
          target: [0, 0.0, -1],
        },
        {
          position: [5, 25, -5],
          target: [0, 0, 0.5],
        },
        {
          position: [5, 50, -5],
          target: [0, 0, -1],
        },
        {
          position: [2, 50, -5],
          target: [0, 0, 0.5],
        },
        {
          position: [5, 80, -5],
          target: [0, 0, 0],
        },
        {
          position: [100, 80, -5],
          target: [100, 0, 0],
        },
        {
          position: [100, 85, -5],
          target: [100, 1, 0],
        },
      ]),
      fade: (ctx) => {
        const value = macaroniBoxSceneFadeOut.evaluate(
          ctx.sceneContext.sceneNowInBeats,
        );
        return [1.0, 1.0, 1.0, value];
      },
    },
  ]);

const lasagaFadeInOut = new PieceFn()
  .addSegment(0, 4, (t) => 1 - easeInCubic(t))
  .addSegment(4, 60, () => 0)
  .addSegment(60, 64, (t) => easeInCubic(t));

const lasagaWorldScene = macaroniBoxScene
  .then({
    length: 64,
    lighting: {
      ...defaultLighting,
      ambient: [0.7843137255, 60 / 255, 43 / 255],
      cameraLightIntensity: [249 / 255, 134 / 255, 38 / 255].map(
        (x) => x * 2.0,
      ) as vec3,
      fogDensity: 2.8,
      light1: {
        position: [0, 10, 0],
        intensity: [0.7843137255, 60 / 255, 43 / 255].map(
          (x) => x * 8.0,
        ) as vec3,
      },
    },
    name: "Lasaga World",
    shader: "lasaga",
    variables: (ctx) => {
      const leadSignal = synthPatternFuzz.evaluate(ctx.sceneNowInBeats);
      const compress = compressLasaga.evaluate(ctx.sceneNowInBeats);
      return [leadSignal, compress, 0, 0];
    },
    text: [
      {
        text: "CHAPTER 3",
        position: [-0.9, 0.8],
        font: "novarese",
        align: "left",
        timing: {
          enter: 8,
          exit: 22,
        },
        type: "horizontal",
      },
      {
        text: "FLIGHT TO FREEDOM",
        position: [-0.9, 0.7],
        timing: {
          enter: 8,
          exit: 22,
        },
        scale: [0.001, 0.001],
        font: "novarese",
        align: "left",
        type: "horizontal",
      },
    ],
  })
  .addSequence([
    {
      length: 64,
      fade: (ctx) => {
        const intensity = lasagaFadeInOut.evaluate(
          ctx.sceneContext.sceneNowInBeats,
        );
        return [1, 1, 1, intensity];
      },
      camera: createCameraPath([
        {
          position: [20, 20, -20],
          target: [0, 8.0, 0],
        },
        {
          position: [-20, 1, 10],
          target: [0, 5.0, 0],
          zoom: 0.5,
        },
        {
          position: [25, 12, 15],
          target: [0, 1, 0],
        },
        {
          position: [5, 24, -5],
          target: [0, 0, 0],
          zoom: 1.3,
        },
        {
          position: [5, 45, -5],
          target: [0, 2, 0],
        },
      ]),
    },
  ]);

const whiteFlashFn = new PieceFn()
  .addSegment(0, 2, (t) => 1 - easeInCubic(t))
  .addSegment(10, 12, (t) => Math.abs(Math.sin(t * Math.PI * 4)))
  .addSegment(12, 20, () => 0)
  .addSegment(22, 24, (t) => Math.abs(Math.sin(t * Math.PI * 16)))
  .addSegment(30, 32, (t) => Math.abs(Math.sin(t * Math.PI * 8)))
  .addSegment(32, 50, () => 0);

const beatFlash = new StatefulSmoothStepSequencer(
  0.5,
  8,
  // prettier-ignore
  [
    1, 0, 0, 0, 0, 0, 0, 0,
    1, 0, 0, 0, 0, 0, 0, 0,
    1, 0, 0, 0, 0, 0, 0, 0,
    1, 0, 0, 0, 0, 0, 0, 0,
    1, 0, 0, 0, 0, 0, 0, 0,
    1, 0, 0, 0, 0, 0, 0, 0,
    1, 0, 0, 0, 0, 0, 0, 0,
    1, 0, 0, 0, 0, 0, 0, 0,
  ],
);

const tesseractBendFn = new PieceFn()
  .addSegment(0, 32, () => 0)
  .addSegment(32, 64, (t) => 0.1 + easeOutCubic(easeInSine(t)) * 0.1);
const tesseractBendFn2 = new PieceFn()
  .addSegment(0, 32, () => 0)
  .addSegment(32, 48, (t) => t * 1.0)
  .addSegment(48, 56, (t) => 3 + t)
  .addSegment(56, 64, (t) => 4 + easeOutCubic(t) * 3.0);

const tesseractRingDistance = new PieceFn()
  // From 8 to 4
  .addSegment(0, 4, (t) => 16.0 - easeInCubic(t) * 12.0)
  // From 4 to 2
  .addSegment(4, 5, (t) => 4.0 - easeOutCubic(t) * 2.0)
  // From 2 to 3
  .addSegment(5, 8, (t) => 2.0 + easeInOutCubic(t))
  // From 3 to 2
  .addSegment(8, 10, (t) => 3.0 - easeOutCubic(t))
  // And back to 4
  .addSegment(10, 12, (t) => 2.0 + easeInCubic(t) * 2.0)
  // 4 to 2
  .addSegment(24, 32, (t) => 4.0 - easeOutCubic(t) * 2.0);

const tesseractRingVerticalDistance = new PieceFn()
  .addSegment(0, 10, () => 0.0)
  .addSegment(10, 12, (t) => easeInCubic(t) * 2.0)
  .addSegment(32, 64, (t) => 2 + Math.sin(t * 3.0));

const tesseractRingAngle = new CumulativePieceFn()
  .addSegment(0, 4, (t) => t * Math.PI * 4.0)
  // And slow down even more (0.25 to 0.15)
  .addSegment(4, 8, (t) => t * Math.PI * 2.0)
  .addSegment(8, 12, (t) => t * Math.PI * 1.5)
  .addSegment(12, 16, (t) => t * Math.PI * 1.1)
  .addSegment(16, 24, (t) => easeInCubic(t) * Math.PI * 0.9)
  .addSegment(24, 32, (t) => easeInCubic(t) * Math.PI * 0.5);

const tesseractFadeOut = new PieceFn()
  .addSegment(0, 60, () => 0)
  .addSegment(60, 64, (t) => easeInCubic(t));

const pastaTesseractScene = lasagaWorldScene
  .then({
    length: 64,
    lighting: {
      ...defaultLighting,
      fogDensity: 0.5,
      cameraLightIntensity: (ctx) => {
        const beatSignal = beatFlash.evaluate(ctx.sceneNowInBeats);
        return vec3.add(
          [0, 0, 0],
          [0.1, 0.1, 0.1],
          vec3.scale([0, 0, 0], [0.4, 0.4, 0.6], beatSignal * 3.0),
        ) as any;
      },
      ambient: [0.6, 0.4, 0.45],
      light1: {
        position: [0, 0, 0],
        // @ts-ignore
        intensity: (ctx) => {
          const beatSignal = beatFlash.evaluate(ctx.sceneNowInBeats);
          return vec3.scale([0, 0, 0], [1, 0, 0.3], beatSignal * 1.0);
        },
      },
      // @ts-ignore
      fogColor: (ctx) => {
        const startBg: vec3 = [8 / 255, 29 / 255, 74 / 255];
        const endBg: vec3 = [0.1, 0.1, 0.2];
        return vec3.lerp([0, 0, 0], startBg, endBg, ctx.sceneProgress);
      },
    },
    name: "Pasta Tesseract",
    sceneId: SCENE_TESSERACT,
    shader: "tesseract",
    variables: (ctx) => {
      return [
        tesseractBendFn.evaluate(ctx.sceneNowInBeats),
        tesseractBendFn2.evaluate(ctx.sceneNowInBeats),
        tesseractBendFn.evaluate(ctx.sceneNowInBeats),
        0,
      ];
    },
    variables2: (ctx) => {
      return [
        tesseractRingDistance.evaluate(ctx.sceneNowInBeats),
        tesseractRingVerticalDistance.evaluate(ctx.sceneNowInBeats),
        tesseractRingAngle.evaluate(ctx.sceneNowInBeats),
        0,
      ];
    },
    effects: {
      enable: ["vhs"],
    },
    text: [
      {
        text: "CHAPTER 4",
        position: [-0.9, 0.8],
        font: "novarese",
        align: "left",
        timing: {
          enter: 2,
          exit: 16,
        },
        type: "horizontal",
      },
      {
        text: "ABSOLUTE POWER\nCORRUPTS THE MAKARONS\nAND THEY DESTROY\nTHE HYVINKAEAE MAKARON FACILITY",
        position: [-0.9, 0.7],
        timing: {
          enter: 2,
          exit: 16,
        },
        scale: [0.001, 0.001],
        font: "novarese",
        align: "left",
        type: "horizontal",
      },
    ],
  })
  .addSequence([
    {
      length: 16,
      camera: createCameraPath([
        {
          position: [0, 10, 0],
          target: [0, 5, 0],
          rotationY: 0,
          zoom: 1.0,
        },
        {
          position: [-5, 15, 10],
          target: [0, 1, 0],
          rotationY: 0,
          zoom: 0.5,
        },
        {
          position: [10, -20, 15],
          target: [0, 1, 0],
          rotationY: 0,
          zoom: 1,
        },
        {
          position: [30, -30, 18],
          target: [0, 5, 0],
          rotationY: 0,
          zoom: 1.0,
        },
      ]),
      fade: (ctx) => [
        1,
        1,
        1,
        whiteFlashFn.evaluate(ctx.sceneContext.sceneNowInBeats),
      ],
    },
    {
      length: 4,
      camera: {
        position: {
          from: [10, -20, 15],
          to: [0, 10, 0],
        },
        target: {
          from: [0, 0, 0],
          to: [0, 5, 0],
        },
        ease: easeOutCubic,
      },
    },
    {
      assertStart: 20,
      length: 12,
      camera: createCameraPath([
        {
          position: [0, 11, 0],
          target: [0, 6, 0],
          rotationY: 0,
          zoom: 1.0,
        },
        {
          position: [0, 10, 0],
          target: [0, 5, 0],
          rotationY: 0,
          zoom: 0.5,
          roll: 0,
        },
        {
          position: [0, 40, 0],
          target: [0, 100, 0],
          rotationY: 0,
          zoom: 1,
          roll: -10.0,
        },
        {
          position: [0.1, -30, 0],
          target: [0, 5, 0],
          rotationY: 0,
          zoom: 1.0,
        },
      ]),
      fade: (ctx) => [
        1,
        1,
        1,
        whiteFlashFn.evaluate(ctx.sceneContext.sceneNowInBeats),
      ],
    },
    {
      assertStart: 32,
      length: 32,
      camera: createCameraPath([
        {
          position: [5, 0, 5],
          target: [0, 0, 0],
          zoom: 2.0,
          roll: Math.PI * 0.25,
        },
        {
          position: [10, 0, 10],
          target: [0, 1, 0],
          zoom: 0.75,
          roll: Math.PI * 0.5,
        },
        {
          position: [20, -10, 10],
          target: [0, 0, 0],
          zoom: 3.0,
          roll: Math.PI * 2.0,
        },
        {
          position: [10, -20, 11],
          target: [0, 1, 0],
          zoom: 3.75,
          roll: Math.PI * 0.25,
        },
      ]),
      fade: (ctx) => {
        return [
          1,
          1,
          1,
          tesseractFadeOut.evaluate(ctx.sceneContext.sceneNowInBeats),
        ];
      },
    },
  ]);

export { pastaTesseractScene as mainScene };
